import { useMutation } from '@apollo/client';
import React from 'react';

import { Switch, useToast } from '@eluve/components';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { upsertEnableSoundEffectsMutation } from './operations';
import { useTenantUserSettings } from './useTenantUserSettings';

export const EnableSoundEffects: React.FC = () => {
  const toast = useToast();
  const { settings } = useTenantUserSettings();
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();

  const analytics = useAnalytics('user_settings');

  const [upsertEnableSoundEffects] = useMutation(
    upsertEnableSoundEffectsMutation,
    {
      onError: () => {
        toast.error(
          'Failed to update sound effects settings. Please try again.',
        );
      },
      optimisticResponse: (data) => ({
        updateTenantUserSettingsByPk: {
          __typename: 'TenantUserSettings' as const,
          tenantId,
          userId,
          enableSoundEffects: data.enableSoundEffects,
        },
      }),
    },
  );

  const enableSoundEffects = async (checked: boolean) => {
    await upsertEnableSoundEffects({
      variables: {
        enableSoundEffects: checked,
        userId,
        tenantId,
      },
      onCompleted: (data) => {
        if (data.updateTenantUserSettingsByPk) {
          analytics.capture('enable_sound_effects_update', {
            tenantId,
            userId,
            checked,
          });
        }
      },
    });
  };

  return (
    <Switch
      checked={settings?.enableSoundEffects ?? true}
      onCheckedChange={enableSoundEffects}
      size="sm"
      className="data-unchecked:bg-border-tertiary"
      aria-label="Sound Effects"
    />
  );
};
